import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BookingService,
  BookingStore,
  DocumentsStore,
  InstallationsStore,
  VoucherValidationResponse,
} from '@store';
import { Document, SubscriptionPlan } from '@vi-iot/client/index';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-booking-voucher-checkout',
  templateUrl: './booking-voucher-checkout.component.html',
  styleUrls: ['./booking-voucher-checkout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingVoucherCheckoutComponent implements OnInit, OnDestroy {
  @Output() reset = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  voucherInputValue: string;
  isValidVoucherNumber = false;
  validationTriggered = false;
  tosAccepted = false;
  subscriptionIdForRenew: string;
  selectedPlan: SubscriptionPlan;
  document: Document;
  readonly BookingPlans = SubscriptionPlan;
  VoucherValidationResponse = VoucherValidationResponse;
  voucherValidationState: VoucherValidationResponse;

  private ngUnsubscribe$ = new Subject<void>();

  constructor(
    public bookingStore: BookingStore,
    public installationsStore: InstallationsStore,
    public bookingService: BookingService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private documentsStore: DocumentsStore,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParamMap.subscribe((paramMap) => {
      const step = paramMap.get('step');
      const boilerSerial = paramMap.get('boilerSerial');
      if (step === '3') {
        this.subscriptionIdForRenew = boilerSerial;
        this.bookingStore.getAllVitoguideSubscriptions();
        this.bookingStore.selectPlan(SubscriptionPlan.Pro);
      }
    });
    this.bookingStore.selectedPlan$.pipe(first()).subscribe((plan) => {
      this.selectedPlan = plan;
    });
    this.bookingStore.getVoucherValidationState$.subscribe((state) => {
      this.voucherValidationState = state;
    });

    this.documentsStore.getDocuments();
    this.documentsStore.termsOfUseDocument$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((document) => {
        this.document = document;
        this.cdr.detectChanges();
      });
  }

  onInputValueChange(value: string): void {
    this.validationTriggered = false;

    this.voucherInputValue = value.trim();
    this.isValidVoucherNumber = value && value.trim().length > 0;
  }

  onTosCheckboxChange(isTosAccepted: boolean): void {
    this.tosAccepted = isTosAccepted;
  }

  onDisabled(): boolean {
    if (
      this.validationTriggered &&
      this.isValidVoucherNumber &&
      this.voucherValidationState === VoucherValidationResponse.Valid
    ) {
      return !this.tosAccepted;
    } else {
      return true;
    }
  }

  onSubscribeNow(): void {
    if (this.subscriptionIdForRenew) {
      return this.bookingStore.subscribeVoucherPackage(
        this.voucherInputValue,
        this.subscriptionIdForRenew
      );
    } else {
      return this.bookingStore.subscribeVoucherPackage(this.voucherInputValue);
    }
  }

  onSubscribeToTrial(): void {
    this.bookingStore.createTrialSubscription();
  }

  onBack(): void {
    if (!this.subscriptionIdForRenew) {
      this.back.emit();
    } else {
      this.router.navigate(['/booking-overview']);
    }
  }

  onActivateCode(): void {
    this.bookingStore.validateVoucherPackage(this.voucherInputValue);
    this.validationTriggered = true;
  }

  ngOnDestroy(): void {
    this.bookingStore.resetSubscriptionsDetails();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
